// Knowledge Base (Session AFA). Two tabs:
//  - Documents: filter, list, tag and delete the technical documents; ingest new ones by manual
//    upload, a Google Drive pull or a Gmail attachment pull; and watch the background processing
//    queue.
//  - Query: ask a plain-English question over the documents (and, optionally, V2's own service
//    history), read the cited answer, and browse past questions.
// Office/admin only (the route is gated by the office layout). Nothing here bills or emails anyone.
import { RecordPicker } from "../../components/RecordPicker";
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Upload } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StatusPill";
import { KnowledgeDriveTab } from "./KnowledgeDriveTab";
import {
  ContextFields,
  DocumentDeleteModals,
  KnowledgeAnswer,
  KnowledgeNeedsContextPanel,
  KnowledgeSourceList,
  KnowledgeWebNoSourcesNotice,
  KnowledgeWebNotice,
  KnowledgeWebSources,
  TagEditorModal,
  contextToTags,
  emptyContext,
  inputCls,
  labelCls,
  useKnowledgeSourceCards,
  type ContextValue,
} from "./knowledgeUi";
import {
  DOC_TYPES,
  DOC_TYPE_LABEL,
  INGEST_STATUSES,
  INGEST_STATUS_LABEL,
  answerSourceLabel,
  answerSourcePill,
  docTypeLabel,
  docTypePill,
  driveIdFromInput,
  formatFileSize,
  gmailIdFromInput,
  knowledgeShortDate,
  type KnowledgeBulkImportPreview,
  type KnowledgeDocType,
  type KnowledgeDocument,
  type KnowledgeIngestStatus,
  type KnowledgeQueryDetail,
  type KnowledgeQueryListItem,
  type KnowledgeQueryResponse,
  type KnowledgeQueueStatus,
} from "../../lib/knowledgeTypes";

type Tab = "documents" | "query" | "library";

const TABS: Array<{ value: Tab; label: string }> = [
  { value: "documents", label: "Documents" },
  { value: "query", label: "Query" },
  // Session BAV: the Drive manuals library (KnowledgeDriveTab.tsx).
  { value: "library", label: "Manuals library" },
];

const MAX_UPLOAD_MB = 25;
const PAGE_SIZE = 25;

// Debounce a value (used for the free-text filters, so a request does not fire per keystroke).
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function KnowledgePage() {
  const [tab, setTab] = useState<Tab>("documents");

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        className="mb-4"
        title="Knowledge base"
        subtitle="Technical documents filed, tagged and searchable, with a plain-English question box over them."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
              tab === t.value ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "documents" ? (
        <DocumentsTab />
      ) : tab === "query" ? (
        <QueryTab />
      ) : (
        <KnowledgeDriveTab />
      )}
    </div>
  );
}

// ---- Documents tab ----

function DocumentsTab() {
  const [siteId, setSiteId] = useState("");
  const [assetId, setAssetId] = useState("");
  const [docType, setDocType] = useState<KnowledgeDocType | "">("");
  const [status, setStatus] = useState<KnowledgeIngestStatus | "">("");
  const [manufacturerInput, setManufacturerInput] = useState("");
  const [modelInput, setModelInput] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const manufacturer = useDebounced(manufacturerInput, 350);
  const model = useDebounced(modelInput, 350);
  const search = useDebounced(searchInput, 350);
  const [page, setPage] = useState(1);

  const [documents, setDocuments] = useState<KnowledgeDocument[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loadError, setLoadError] = useState(false);

  const [queue, setQueue] = useState<KnowledgeQueueStatus | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [driveOpen, setDriveOpen] = useState(false);
  const [gmailOpen, setGmailOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<KnowledgeDocument | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeDocument | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const loadDocuments = useCallback(() => {
    setLoadError(false);
    api.knowledge.documents
      .list({
        siteId: siteId || undefined,
        assetId: assetId || undefined,
        docType: docType || undefined,
        ingestStatus: status || undefined,
        manufacturer: manufacturer.trim() || undefined,
        liftModel: model.trim() || undefined,
        search: search.trim() || undefined,
        page,
        pageSize: PAGE_SIZE,
      })
      .then((res) => {
        setDocuments(res.items);
        setTotal(res.total);
      })
      .catch(() => setLoadError(true));
  }, [siteId, assetId, docType, status, manufacturer, model, search, page]);

  const loadQueue = useCallback(() => {
    api.knowledge
      .queueStatus()
      .then(setQueue)
      .catch(() => {
        /* the queue panel is best-effort; the list still works without it */
      });
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);
  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  function changeSite(next: string) {
    setSiteId(next);
    // Clear the lift: it belongs to the previously chosen site, and the lift picker is now narrowed to the new one.
    if (assetId && next !== siteId) setAssetId("");
    setPage(1);
  }

  async function retry(id: string) {
    setRetryingId(id);
    try {
      await api.knowledge.documents.retry(id);
      loadQueue();
      loadDocuments();
    } catch {
      /* the failure stays listed; the office can retry again */
    } finally {
      setRetryingId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
        <Button onClick={() => setUploadOpen(true)} icon={<Upload aria-hidden size={15} />}>
          Upload
        </Button>
        <button
          onClick={() => setDriveOpen(true)}
          className="max-sm:min-h-11 rounded-ele border border-ele-blue px-4 py-2.5 font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
        >
          Pull from Drive
        </button>
        <button
          onClick={() => setGmailOpen(true)}
          className="max-sm:min-h-11 rounded-ele border border-ele-blue px-4 py-2.5 font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
        >
          Pull from Gmail
        </button>
        <button
          onClick={() => setBulkOpen(true)}
          className="max-sm:min-h-11 rounded-ele border border-ele-blue px-4 py-2.5 font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue/10"
        >
          Bulk import from Drive folder
        </button>
      </div>

      {banner && (
        <div role="status" className="mb-4 flex items-start justify-between gap-3 rounded-ele border border-ele-green bg-ele-surface p-4">
          <p className="text-sm text-ele-body">{banner}</p>
          <button type="button" onClick={() => setBanner(null)} className="text-xs text-ele-body underline hover:text-ele-strong">
            Dismiss
          </button>
        </div>
      )}

      {/* The search is the server's own (filenames and summaries); the toolbar is its shared home.
          Clear resets every filter below as well as the search, since the server applies all of them. */}
      <ListToolbar
        className="mb-3"
        search={searchInput}
        onSearchChange={(v) => {
          setSearchInput(v);
          setPage(1);
        }}
        searchPlaceholder="Search filenames and summaries"
        searchLabel="Search filenames and summaries"
        onClear={() => {
          setSearchInput("");
          setSiteId("");
          setAssetId("");
          setDocType("");
          setStatus("");
          setManufacturerInput("");
          setModelInput("");
          setPage(1);
        }}
      />

      {/* Filter bar */}
      <div className="mb-4 grid gap-3 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <RecordPicker kind="site" label="Site" value={siteId} placeholder="All sites (search to filter)" onChange={(id) => changeSite(id)} />
        </div>
        <div>
          <RecordPicker
            kind="lift"
            label="Lift"
            value={assetId}
            placeholder="All lifts (search to filter)"
            filters={siteId ? { siteId } : undefined}
            onChange={(id) => {
              setAssetId(id);
              setPage(1);
            }}
          />
        </div>
        <label>
          <span className={labelCls}>Document type</span>
          <select
            aria-label="Filter by document type"
            value={docType}
            onChange={(e) => {
              setDocType(e.target.value as KnowledgeDocType | "");
              setPage(1);
            }}
            className={inputCls}
          >
            <option value="">All types</option>
            {DOC_TYPES.map((t) => (
              <option key={t} value={t}>
                {DOC_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={labelCls}>Status</span>
          <select
            aria-label="Filter by status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as KnowledgeIngestStatus | "");
              setPage(1);
            }}
            className={inputCls}
          >
            <option value="">All statuses</option>
            {INGEST_STATUSES.map((s) => (
              <option key={s} value={s}>
                {INGEST_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={labelCls}>Manufacturer</span>
          <input
            aria-label="Filter by manufacturer"
            value={manufacturerInput}
            onChange={(e) => {
              setManufacturerInput(e.target.value);
              setPage(1);
            }}
            className={inputCls}
          />
        </label>
        <label>
          <span className={labelCls}>Model</span>
          <input
            aria-label="Filter by model"
            value={modelInput}
            onChange={(e) => {
              setModelInput(e.target.value);
              setPage(1);
            }}
            className={inputCls}
          />
        </label>
      </div>

      {loadError ? (
        <div role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-6 text-center">
          <p className="text-ele-strong">Could not load the documents. Please try again.</p>
          <button onClick={loadDocuments} className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong">
            Retry
          </button>
        </div>
      ) : documents === null ? (
        <Loader label="Loading the documents..." />
      ) : documents.length === 0 ? (
        <EmptyState message="No documents yet. Use Upload, Pull from Drive or Pull from Gmail to add the first one." />
      ) : (
        <DocumentsTable
          rows={documents}
          onEdit={setEditTarget}
          onDelete={setDeleteTarget}
        />
      )}

      {documents !== null && documents.length > 0 && totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between gap-3 text-sm text-ele-body">
          <span>
            Page {page} of {totalPages} ({total} document{total === 1 ? "" : "s"})
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <QueuePanel queue={queue} onRefresh={loadQueue} onRetry={retry} retryingId={retryingId} />

      {uploadOpen && (
        <UploadModal
         
          onClose={() => setUploadOpen(false)}
          onDone={(msg) => {
            setUploadOpen(false);
            setBanner(msg);
            loadDocuments();
            loadQueue();
          }}
        />
      )}
      {driveOpen && (
        <DrivePullModal
         
          onClose={() => setDriveOpen(false)}
          onDone={(msg) => {
            setDriveOpen(false);
            setBanner(msg);
            loadDocuments();
            loadQueue();
          }}
        />
      )}
      {gmailOpen && (
        <GmailPullModal
         
          onClose={() => setGmailOpen(false)}
          onDone={(msg) => {
            setGmailOpen(false);
            setBanner(msg);
            loadDocuments();
            loadQueue();
          }}
        />
      )}
      {bulkOpen && (
        <BulkImportModal
         
          onClose={() => setBulkOpen(false)}
          onDone={(msg) => {
            setBulkOpen(false);
            setBanner(msg);
            loadDocuments();
            loadQueue();
          }}
        />
      )}
      {editTarget && (
        <TagEditorModal
          doc={editTarget}
         
          onClose={() => setEditTarget(null)}
          onSaved={(updated) => {
            setDocuments((docs) => (docs ? docs.map((d) => (d.id === updated.id ? updated : d)) : docs));
            setEditTarget(null);
          }}
        />
      )}
      <DocumentDeleteModals
        doc={deleteTarget}
        onClose={() => {
          setDeleteTarget(null);
          // Cancelling deletes nothing now (the delete only commits on the final confirm), but a
          // refresh keeps the list honest if another user changed something meanwhile.
          loadDocuments();
          loadQueue();
        }}
        onDeleted={(id) => {
          setDocuments((docs) => (docs ? docs.filter((d) => d.id !== id) : docs));
          setDeleteTarget(null);
          loadQueue();
        }}
      />
    </div>
  );
}

// Exported for its focused component test (the BulkImportModal/QueryTab precedent above).
export function DocumentsTable({
  rows,
  onEdit,
  onDelete,
}: {
  rows: KnowledgeDocument[];
  onEdit: (d: KnowledgeDocument) => void;
  onDelete: (d: KnowledgeDocument) => void;
}) {
  // Card list below md, table otherwise -- decided once at mount (the InvoicesPage mechanism).
  // jsdom has no matchMedia, so every existing test still renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);

  return mobileCards ? (
    /* Filename, Type, Status and Actions are the surviving desktop-priority columns (Site/Lift,
       Manufacturer and Model are already max-md:hidden below), so the card keeps that same shape:
       filename never truncates, the two pills come back into the right rail, and the three
       formerly-hidden fields move into details where each gets its own full line. The whole card
       taps through to the detail page exactly as the Filename link already does, so "View" is not
       repeated as a separate action -- only the two controls that do not already have a tap target. */
    <MobileCardList label="Documents">
      {rows.map((d) => (
        <MobileListCard
          key={d.id}
          to={`/office/knowledge/${d.id}`}
          primary={d.fileName}
          secondary={formatFileSize(d.fileSize)}
          meta={d.siteName ? (d.eleNumber ? `${d.siteName} (${d.eleNumber})` : d.siteName) : "Not linked"}
          right={
            <>
              <StatusPill word={docTypeLabel(d.docType)} tone={docTypePill()} />
              <StatusPill domain="knowledgeIngest" status={d.ingestStatus} />
            </>
          }
          details={
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
              <dt className="text-ele-body">Manufacturer</dt>
              <dd className="text-ele-body">{d.manufacturer ?? "Not set"}</dd>
              <dt className="text-ele-body">Model</dt>
              <dd className="text-ele-body">{d.liftModel ?? "Not set"}</dd>
              <dt className="text-ele-body">Uploaded</dt>
              <dd className="text-ele-body">{knowledgeShortDate(d.createdAt)}</dd>
            </dl>
          }
          actions={
            <>
              <button
                type="button"
                onClick={() => onEdit(d)}
                className="min-h-11 rounded-ele px-2 font-menu text-xs uppercase tracking-wide text-ele-body underline hover:text-ele-strong"
              >
                Edit tags
              </button>
              <button
                type="button"
                onClick={() => onDelete(d)}
                className="min-h-11 rounded-ele px-2 font-menu text-xs uppercase tracking-wide text-ele-danger underline"
              >
                Delete
              </button>
            </>
          }
        />
      ))}
    </MobileCardList>
  ) : (
    <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
      <table className="ele-list-table w-full text-left text-sm">
        <colgroup>
          <col style={{ width: "22%" }} />
          <col style={{ width: "12%" }} />
          <col className="max-md:hidden" style={{ width: "15%" }} />
          <col className="max-md:hidden" style={{ width: "12%" }} />
          <col className="max-md:hidden" style={{ width: "9%" }} />
          <col style={{ width: "10%" }} />
          <col className="max-md:hidden" style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
        </colgroup>
        <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
          <tr>
            <th className="px-3 py-2">Filename</th>
            <th className="px-3 py-2">Type</th>
            <th className="max-md:hidden px-3 py-2">Site / Lift</th>
            <th className="max-md:hidden px-3 py-2">Manufacturer</th>
            <th className="max-md:hidden px-3 py-2">Model</th>
            <th className="px-3 py-2">Status</th>
            <th className="max-md:hidden px-3 py-2">Uploaded</th>
            <th className="px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.id} className="border-t border-ele-hairline">
              <td className="px-3 py-2">
                <Link to={`/office/knowledge/${d.id}`} className="break-all font-semibold text-ele-strong hover:text-ele-green">
                  {d.fileName}
                </Link>
                <div className="text-xs text-ele-body opacity-70">{formatFileSize(d.fileSize)}</div>
              </td>
              <td className="px-3 py-2">
                <StatusPill word={docTypeLabel(d.docType)} tone={docTypePill()} />
              </td>
              <td className="max-md:hidden px-3 py-2 text-ele-body">
                <div>{d.siteName ?? "Not linked"}</div>
                {d.eleNumber && <div className="text-xs opacity-70">{d.eleNumber}</div>}
              </td>
              <td className="max-md:hidden px-3 py-2 text-ele-body">{d.manufacturer ?? "Not set"}</td>
              <td className="max-md:hidden px-3 py-2 text-ele-body">{d.liftModel ?? "Not set"}</td>
              <td className="px-3 py-2">
                <StatusPill domain="knowledgeIngest" status={d.ingestStatus} />
              </td>
              <td className="max-md:hidden px-3 py-2 text-ele-body">{knowledgeShortDate(d.createdAt)}</td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-3 text-xs uppercase tracking-wide">
                  <Link to={`/office/knowledge/${d.id}`} className="font-menu text-ele-blue underline">
                    View
                  </Link>
                  <button type="button" onClick={() => onEdit(d)} className="font-menu text-ele-body underline hover:text-ele-strong">
                    Edit tags
                  </button>
                  <button type="button" onClick={() => onDelete(d)} className="font-menu text-ele-danger underline">
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QueuePanel({
  queue,
  onRefresh,
  onRetry,
  retryingId,
}: {
  queue: KnowledgeQueueStatus | null;
  onRefresh: () => void;
  onRetry: (id: string) => void;
  retryingId: string | null;
}) {
  if (!queue) return null;
  return (
    <section className="mt-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg text-ele-blue">Processing queue</h2>
        <button onClick={onRefresh} className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong">
          Refresh
        </button>
      </div>
      <p className="mt-1 text-sm text-ele-body">
        {queue.queued} queued, {queue.processing} processing, {queue.failed} failed.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <StatusPill word={`${queue.queued} queued`} tone="grey" />
        <StatusPill word={`${queue.processing} processing`} tone="amber" />
        <StatusPill word={`${queue.complete} ready`} tone="green" />
        <StatusPill word={`${queue.failed} failed`} tone={queue.failed > 0 ? "red" : "grey"} />
      </div>
      {queue.recentFailures.length > 0 && (
        <div className="mt-4">
          <h3 className="mb-2 font-menu text-xs uppercase tracking-wide text-ele-danger">Recent failures</h3>
          <ul className="space-y-2">
            {queue.recentFailures.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center justify-between gap-3 rounded-ele border border-ele-danger bg-ele-inset px-3 py-2 text-sm">
                <div className="min-w-0">
                  <Link to={`/office/knowledge/${f.id}`} className="break-all font-semibold text-ele-strong hover:text-ele-green">
                    {f.fileName}
                  </Link>
                  <p className="text-xs text-ele-danger">{f.ingestError ?? "Processing failed."}</p>
                </div>
                <button
                  type="button"
                  onClick={() => onRetry(f.id)}
                  disabled={retryingId === f.id}
                  className="max-sm:min-h-11 shrink-0 rounded-ele border border-ele-blue px-3 py-1.5 font-menu text-xs tracking-wide text-ele-blue hover:bg-ele-blue/10 disabled:opacity-50"
                >
                  {retryingId === f.id ? "Retrying..." : "Retry"}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ---- Ingest modals ----

function IngestModalShell({
  title,
  onClose,
  onSubmit,
  busy,
  error,
  submitLabel,
  children,
}: {
  title: string;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  busy: boolean;
  error: string | null;
  submitLabel: string;
  children: ReactNode;
}) {
  return (
    <ModalShell ariaLabel={title} onClose={() => !busy && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <form onSubmit={onSubmit}>
        <h2 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">{title}</h2>
        <div className="grid gap-3 sm:grid-cols-2">{children}</div>
        {error && (
          <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {error}
          </p>
        )}
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}
          >
            Cancel
          </button>
          <Button type="submit" disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Working..." : submitLabel}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function UploadModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [ctx, setCtx] = useState<ContextValue>(emptyContext);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!file) {
      setError("Choose a file to upload.");
      return;
    }
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setError(`That file is over ${MAX_UPLOAD_MB}MB. Please upload a smaller one.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.knowledge.documents.uploadFile(file, contextToTags(ctx));
      onDone(res.message || "Queued for processing.");
    } catch (err) {
      setError(friendlyApiError(err, "Could not upload the file. Please try again."));
      setBusy(false);
    }
  }

  return (
    <IngestModalShell title="Upload a document" onClose={onClose} onSubmit={submit} busy={busy} error={error} submitLabel="Upload">
      <label className="sm:col-span-2">
        <span className={labelCls}>File</span>
        <input
          type="file"
          aria-label="File to upload"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="w-full text-sm text-ele-body file:mr-3 file:rounded-ele file:border-0 file:bg-ele-green file:px-4 file:py-2 file:font-menu file:text-xs file:uppercase file:tracking-wide file:text-ele-bg"
        />
        <p className="mt-1 text-xs text-ele-body opacity-70">Up to {MAX_UPLOAD_MB}MB. It is queued for processing and appears in the list straight away.</p>
      </label>
      <ContextFields value={ctx} onChange={setCtx} idPrefix="upload" />
    </IngestModalShell>
  );
}

function DrivePullModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [kind, setKind] = useState<"file" | "folder">("file");
  const [raw, setRaw] = useState("");
  const [ctx, setCtx] = useState<ContextValue>(emptyContext);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const id = driveIdFromInput(raw, kind);
    if (!id) {
      setError("Paste a Google Drive link or id.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.knowledge.documents.fromDrive({
        ...(kind === "file" ? { fileId: id } : { folderId: id }),
        ...contextToTags(ctx),
      });
      onDone(res.message || `Queued ${res.created} document(s).`);
    } catch (err) {
      setError(friendlyApiError(err, "Could not pull from Drive. Please try again."));
      setBusy(false);
    }
  }

  return (
    <IngestModalShell title="Pull from Google Drive" onClose={onClose} onSubmit={submit} busy={busy} error={error} submitLabel="Pull from Drive">
      <div className="sm:col-span-2">
        <span className={labelCls}>What are you pulling?</span>
        <div className="flex flex-wrap gap-4 text-sm text-ele-body">
          <label className="flex items-center gap-2">
            <input type="radio" name="drive-kind" checked={kind === "file"} onChange={() => setKind("file")} className="accent-ele-green" />
            A single file
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="drive-kind" checked={kind === "folder"} onChange={() => setKind("folder")} className="accent-ele-green" />
            Every file in a folder
          </label>
        </div>
      </div>
      <label className="sm:col-span-2">
        <span className={labelCls}>{kind === "file" ? "Drive file link or id" : "Drive folder link or id"}</span>
        <input
          aria-label={kind === "file" ? "Drive file link or id" : "Drive folder link or id"}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="Paste the share link or the id"
          className={inputCls}
        />
      </label>
      <ContextFields value={ctx} onChange={setCtx} idPrefix="drive" />
    </IngestModalShell>
  );
}

function GmailPullModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [kind, setKind] = useState<"message" | "thread">("message");
  const [raw, setRaw] = useState("");
  const [ctx, setCtx] = useState<ContextValue>(emptyContext);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const id = gmailIdFromInput(raw);
    if (!id) {
      setError("Paste a Gmail message or thread id.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.knowledge.documents.fromGmail({
        ...(kind === "message" ? { messageId: id } : { threadId: id }),
        ...contextToTags(ctx),
      });
      onDone(res.message || `Queued ${res.created} attachment(s).`);
    } catch (err) {
      setError(friendlyApiError(err, "Could not pull from Gmail. Please try again."));
      setBusy(false);
    }
  }

  return (
    <IngestModalShell title="Pull from Gmail" onClose={onClose} onSubmit={submit} busy={busy} error={error} submitLabel="Pull from Gmail">
      <div className="sm:col-span-2">
        <span className={labelCls}>What are you pulling?</span>
        <div className="flex flex-wrap gap-4 text-sm text-ele-body">
          <label className="flex items-center gap-2">
            <input type="radio" name="gmail-kind" checked={kind === "message"} onChange={() => setKind("message")} className="accent-ele-green" />
            One message
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="gmail-kind" checked={kind === "thread"} onChange={() => setKind("thread")} className="accent-ele-green" />
            A whole thread
          </label>
        </div>
        <p className="mt-2 text-xs text-ele-body opacity-70">The attachments on the message or thread are imported. Emails with no attachments are skipped.</p>
      </div>
      <label className="sm:col-span-2">
        <span className={labelCls}>{kind === "message" ? "Gmail message id" : "Gmail thread id"}</span>
        <input
          aria-label={kind === "message" ? "Gmail message id" : "Gmail thread id"}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder="Paste the id"
          className={inputCls}
        />
      </label>
      <ContextFields value={ctx} onChange={setCtx} idPrefix="gmail" />
    </IngestModalShell>
  );
}

// Bulk import from a Drive folder: a two-step flow so the office sees the PDF count and the estimated
// embedding cost BEFORE anything is queued. Step 1 previews (reads nothing into the knowledge base);
// step 2 queues up to the 200-per-run cap. The queue panel already shows progress once queued.
// Exported for its focused component test.
export function BulkImportModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [folder, setFolder] = useState("");
  const [ctx, setCtx] = useState<ContextValue>(emptyContext);
  const [preview, setPreview] = useState<KnowledgeBulkImportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function check(e: FormEvent) {
    e.preventDefault();
    if (!folder.trim()) {
      setError("Paste a Google Drive folder link or id.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.knowledge.documents.bulkImportPreview({ folder: folder.trim(), ...contextToTags(ctx) });
      setPreview(res);
    } catch (err) {
      setError(friendlyApiError(err, "Could not read that folder. Please check the link and that Google is connected."));
    } finally {
      setBusy(false);
    }
  }

  async function confirmQueue() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.knowledge.documents.bulkImport({ folder: folder.trim(), ...contextToTags(ctx) });
      onDone(res.message || `Queued ${res.created} document(s).`);
    } catch (err) {
      setError(friendlyApiError(err, "Could not queue the folder. Please try again."));
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Bulk import from a Drive folder" onClose={() => !busy && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <h2 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">Bulk import from Drive folder</h2>

      {!preview ? (
        <form onSubmit={check}>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="sm:col-span-2">
              <span className={labelCls}>Drive folder link or id</span>
              <input
                aria-label="Drive folder link or id"
                value={folder}
                onChange={(e) => setFolder(e.target.value)}
                placeholder="Paste the folder share link or the id"
                className={inputCls}
              />
              <p className="mt-1 text-xs text-ele-body opacity-70">Every PDF directly in the folder is queued, up to 200 per run. These tags apply to all documents in this folder.</p>
            </label>
            <ContextFields value={ctx} onChange={setCtx} idPrefix="bulk" />
          </div>
          {error && (
            <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {error}
            </p>
          )}
          <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              onClick={onClose}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}
            >
              Cancel
            </button>
            <Button type="submit" disabled={busy} className={MODAL_ACTION_BTN}>
              {busy ? "Checking..." : "Check folder"}
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <p className="text-sm text-ele-body">{preview.message}</p>
          {preview.fileCount > 0 && (
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-sm">
              <dt className="text-ele-body">PDF files in folder</dt>
              <dd className="text-right text-ele-strong">{preview.folderTotal}</dd>
              {preview.alreadyImported > 0 && (
                <>
                  <dt className="text-ele-body">Already imported</dt>
                  <dd className="text-right text-ele-strong">{preview.alreadyImported}</dd>
                </>
              )}
              <dt className="text-ele-body">To import now</dt>
              <dd className="text-right text-ele-strong">{preview.willQueue}</dd>
              <dt className="text-ele-body">Estimated tokens</dt>
              <dd className="text-right text-ele-strong">{preview.estimatedTokens.toLocaleString("en-GB")}</dd>
              <dt className="text-ele-body">Estimated cost</dt>
              <dd className="text-right text-ele-strong">
                approx ${preview.estimatedCostUsd.toFixed(2)}
                <span className="block text-xs text-ele-body opacity-70">at ${preview.ratePerMillionUsd.toFixed(2)} per million tokens</span>
              </dd>
            </dl>
          )}
          {preview.overCap && (
            <p className="mt-3 rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-ele-strong">
              More than the {preview.cap} per-run limit is still to import. Only {preview.willQueue} will queue now. Run the import again to queue the next {preview.cap}.
            </p>
          )}
          {error && (
            <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {error}
            </p>
          )}
          <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setPreview(null);
                setError(null);
              }}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong disabled:opacity-50 ${MODAL_ACTION_BTN}`}
            >
              Back
            </button>
            <Button disabled={busy || preview.willQueue === 0} onClick={confirmQueue} className={MODAL_ACTION_BTN}>
              {busy ? "Queueing..." : `Queue ${preview.willQueue} document${preview.willQueue === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ---- Query tab ----

// Exported for its focused component test (the BulkImportModal precedent above).
export function QueryTab() {
  const [question, setQuestion] = useState("");
  const [ctx, setCtx] = useState<ContextValue>(emptyContext);
  const [includeV2, setIncludeV2] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<KnowledgeQueryResponse | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(true);
  const cards = useKnowledgeSourceCards();

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<KnowledgeQueryListItem[] | null>(null);
  const [historyError, setHistoryError] = useState(false);
  const [detailTarget, setDetailTarget] = useState<KnowledgeQueryDetail | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);

  const loadHistory = useCallback(() => {
    setHistoryError(false);
    api.knowledge.queries
      .list(1, 10)
      .then((r) => setHistory(r.items))
      .catch(() => setHistoryError(true));
  }, []);
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Split out from the submit handler so the needs-context panel's "Search again" can re-run the same
  // question against whatever is now in the context picker, without a synthetic form event.
  async function runQuery() {
    if (!question.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const context = {
        siteId: ctx.siteId || undefined,
        assetId: ctx.assetId || undefined,
        manufacturer: ctx.manufacturer.trim() || undefined,
        liftModel: ctx.liftModel.trim() || undefined,
      };
      const hasContext = Boolean(context.siteId || context.assetId || context.manufacturer || context.liftModel);
      const res = await api.knowledge.query({
        question: question.trim(),
        includeV2History: includeV2,
        ...(hasContext ? { context } : {}),
      });
      setResult(res);
      setSourcesOpen(true);
      // A new answer starts with every source card collapsed again.
      cards.reset();
      loadHistory();
    } catch (err) {
      setError(friendlyApiError(err, "Could not run that question. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  function ask(e: FormEvent) {
    e.preventDefault();
    void runQuery();
  }

  // A citation marker opens its card even when the reader has collapsed the whole sources block. The
  // scroll itself is deferred inside the hook until the card is on the page.
  function showSource(index: number) {
    setSourcesOpen(true);
    cards.reveal(index);
  }

  // The needs-context prompt points at the context picker's own Manufacturer field rather than
  // offering a second copy of it.
  function focusEquipment() {
    const el = typeof document === "undefined" ? null : document.getElementById("query-manufacturer");
    el?.scrollIntoView?.({ block: "center" });
    (el as HTMLInputElement | null)?.focus?.();
  }

  async function openDetail(id: string) {
    setDetailBusy(true);
    try {
      const res = await api.knowledge.queries.get(id);
      setDetailTarget(res.query);
    } catch {
      /* leave the history row as-is; the office can try again */
    } finally {
      setDetailBusy(false);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div>
        <form onSubmit={ask} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
          <label>
            <span className={labelCls}>Your question</span>
            <textarea
              aria-label="Your question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={4}
              placeholder="e.g. What has caused repeat door faults on Kone MonoSpace lifts?"
              className={inputCls}
            />
          </label>
          <p className="mb-3 mt-1 text-xs text-ele-body opacity-70">Narrow the answer to a site, lift, manufacturer or model (all optional).</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <ContextFields value={ctx} onChange={setCtx} idPrefix="query" />
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-ele-body">
            <input type="checkbox" checked={includeV2} onChange={(e) => setIncludeV2(e.target.checked)} className="accent-ele-green" />
            Include ELE service history (call-outs, defects and LOLER records) in the answer
          </label>
          <div className="mt-4">
            <Button type="submit" disabled={busy || !question.trim()} className="max-sm:w-full">
              {busy ? "Thinking..." : "Ask"}
            </Button>
          </div>
        </form>

        {error && (
          <p role="alert" className="mt-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-3 text-sm text-ele-strong">
            {error}
          </p>
        )}

        {result && (
          <div className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-heading text-lg text-ele-green">Answer</h2>
                <StatusPill word={answerSourceLabel(result.answerSource)} tone={answerSourcePill(result.answerSource)} />
              </div>
              <button
                type="button"
                onClick={() => window.print()}
                className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
              >
                Save as PDF
              </button>
            </div>

            {result.status === "needs_context" ? (
              <KnowledgeNeedsContextPanel
                message={result.answer}
                onEditEquipment={focusEquipment}
                onSearchAgain={() => void runQuery()}
                busy={busy}
              />
            ) : (
              <>
                {result.answerSource === "web_search" && <KnowledgeWebNotice />}

                <KnowledgeAnswer answer={result.answer} sourceCount={result.sources.length} onCite={showSource} />

                <p className="mt-3 text-xs text-ele-body opacity-70">
                  {result.v2HistoryUsed ? "ELE service history was used in this answer." : "ELE service history was not used in this answer."}
                  {" | "}
                  {result.tokensUsed.toLocaleString("en-GB")} tokens used.
                  {result.webSearchQuery ? ` | Web search run: ${result.webSearchQuery}` : ""}
                </p>

                {/* A web answer with no citations at all is the model's own general knowledge, not
                    something it read. Say so in place of the (empty) list rather than leaving a
                    silent gap that reads as sourced. The main amber notice above stays. */}
                {result.answerSource === "web_search" && result.webSources.length === 0 ? (
                  <KnowledgeWebNoSourcesNotice />
                ) : (
                  <KnowledgeWebSources sources={result.webSources} />
                )}

                <div className="mt-3 border-t border-ele-hairline pt-3">
                  <button
                    type="button"
                    onClick={() => setSourcesOpen((v) => !v)}
                    aria-expanded={sourcesOpen}
                    aria-controls="kb-sources"
                    className="ele-kb-src-toggle-hint font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong"
                  >
                    {sourcesOpen ? "Hide" : "Show"} sources ({result.sources.length})
                  </button>
                  {/* Hidden with CSS rather than unmounted, so the print rule can bring the sources
                      back even if the reader collapsed them before pressing Save as PDF. */}
                  <div id="kb-sources" className={sourcesOpen ? "" : "ele-kb-src-body--closed"}>
                    <KnowledgeSourceList sources={result.sources} cards={cards} />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Query history */}
      <div>
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-2 text-left"
            aria-expanded={historyOpen}
          >
            <span className="font-heading text-lg text-ele-blue">Query history</span>
            <span className="font-menu text-xs uppercase tracking-wide text-ele-body">{historyOpen ? "Hide" : "Show"}</span>
          </button>
          {historyOpen && (
            <div className="mt-3">
              {historyError ? (
                <div role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-3 text-sm text-ele-strong">
                  Could not load the query history.
                  <button onClick={loadHistory} className="ml-2 underline hover:text-ele-green">
                    Retry
                  </button>
                </div>
              ) : history === null ? (
                <Loader label="Loading the query history..." shape="inline" />
              ) : history.length === 0 ? (
                <p className="text-sm text-ele-body opacity-70">No questions have been asked yet.</p>
              ) : (
                <ul className="space-y-2">
                  {history.map((q) => (
                    <li key={q.id} className="rounded-ele border border-ele-hairline bg-ele-inset px-3 py-2 text-sm">
                      <p className="text-ele-strong">{q.queryText}</p>
                      {/* Where the answer came from, on every row: the point of recording it is that
                          the office can scan the history for the faults the knowledge base could not
                          answer, and go and ingest documents for them. */}
                      <div className="mt-1">
                        <StatusPill word={answerSourceLabel(q.answerSource)} tone={answerSourcePill(q.answerSource)} />
                      </div>
                      <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs text-ele-body">
                        <span>
                          {q.askedByName ?? "Unknown"} | {knowledgeShortDate(q.createdAt)}
                        </span>
                        <button
                          type="button"
                          onClick={() => openDetail(q.id)}
                          disabled={detailBusy}
                          className="font-menu tracking-wide text-ele-blue underline disabled:opacity-50"
                        >
                          View answer
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {detailTarget && <QueryDetailModal detail={detailTarget} onClose={() => setDetailTarget(null)} />}
    </div>
  );
}

function QueryDetailModal({ detail, onClose }: { detail: KnowledgeQueryDetail; onClose: () => void }) {
  return (
    <ModalShell ariaLabel="Query answer" onClose={onClose} width="max-w-2xl" margin="my-8" panelClassName="p-6">
      <h2 className="mb-1 font-heading text-2xl tracking-wide text-ele-strong">Question</h2>
      <p className="mb-4 text-sm text-ele-body">{detail.queryText}</p>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-lg text-ele-green">Answer</h3>
        <StatusPill word={answerSourceLabel(detail.answerSource)} tone={answerSourcePill(detail.answerSource)} />
      </div>
      {/* A stored answer can contain [N] markers, but the recorded row carries no sources array for
          them to point at. Passing a source count of zero renders every marker as ordinary text, which
          is what parseAnswerSegments already does for an out-of-range marker: nothing is stripped and
          nothing links to a card that is not there. */}
      <KnowledgeAnswer answer={detail.answerText} sourceCount={0} />
      {detail.webSearchQuery && (
        <p className="mt-2 text-xs text-ele-body">Web search run: {detail.webSearchQuery}</p>
      )}
      <p className="mt-3 text-xs text-ele-body opacity-70">
        {detail.askedByName ?? "Unknown"} | {knowledgeShortDate(detail.createdAt)} | {detail.answerModel}
        {detail.tokensUsed != null ? ` | ${detail.tokensUsed.toLocaleString("en-GB")} tokens` : ""} | {detail.retrievedDocIds.length} document
        {detail.retrievedDocIds.length === 1 ? "" : "s"} referenced
      </p>
      <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
        <button
          type="button"
          onClick={onClose}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}
        >
          Close
        </button>
      </div>
    </ModalShell>
  );
}
